/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { MouseEvent, useState } from 'react';
import useDebounce from 'react-use/esm/useDebounce';
import {
  ALL_RELATION_PAIRS,
  EntityEdge,
  EntityNode,
  RelationPairs,
} from '@backstage/plugin-catalog-graph';
import { Entity, DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import { useEntityRelationGraph } from './useEntityRelationGraph';

type Options = {
  rootEntityRefs: string[];
  maxDepth?: number;
  unidirectional?: boolean;
  mergeRelations?: boolean;
  kinds?: string[];
  relations?: string[];
  entityFilter?: (entity: Entity) => boolean;
  onNodeClick?: (value: EntityNode, event: MouseEvent<unknown>) => void;
  relationPairs?: RelationPairs;
};

export const useEntityRelationNodesAndEdges = ({
  rootEntityRefs,
  maxDepth = Number.POSITIVE_INFINITY,
  unidirectional = true,
  mergeRelations = true,
  kinds,
  relations,
  entityFilter,
  onNodeClick,
  relationPairs = ALL_RELATION_PAIRS,
}: Options): {
  loading: boolean;
  nodes?: EntityNode[];
  edges?: EntityEdge[];
  error?: Error;
} => {
  const [nodesAndEdges, setNodesAndEdges] = useState<{
    nodes?: EntityNode[];
    edges?: EntityEdge[];
  }>({});

  const { entities, loading, error } = useEntityRelationGraph({
    rootEntityRefs,
    filter: {
      maxDepth,
      kinds,
      relations,
      entityFilter,
    },
  });

  useDebounce(
    () => {
      if (!entities || Object.keys(entities).length === 0) {
        setNodesAndEdges({});
        return;
      }

      const nodes = Object.entries(entities).map(([entityRef, entity]) => {
        const focused = rootEntityRefs.includes(entityRef);
        const node: EntityNode = {
          id: entityRef,
          entity,
          focused,
          color: focused ? 'secondary' : 'primary',
          kind: entity.kind,
          name: entity.metadata.name,
          namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
          title: entity.metadata.title,
          spec: entity.spec,
        };

        if (onNodeClick) {
          node.onClick = event => onNodeClick(node, event);
        }

        return node;
      });

      const edges: EntityEdge[] = [];
      const visitedNodes = new Set<string>();
      const nodeQueue = [...rootEntityRefs];

      while (nodeQueue.length > 0) {
        const entityRef = nodeQueue.pop()!;
        const entity = entities[entityRef];
        visitedNodes.add(entityRef);

        if (!entity) {
          continue;
        }

        entity.relations?.forEach(relation => {
          if (!entities[relation.targetRef]) {
            return;
          }

          if (relations && !relations.includes(relation.type)) {
            return;
          }

          if (
            kinds &&
            !kinds.some(kind =>
              relation.targetRef.startsWith(
                `${kind.toLocaleLowerCase('en-US')}:`,
              ),
            )
          ) {
            return;
          }

          if (!unidirectional || !visitedNodes.has(relation.targetRef)) {
            if (mergeRelations) {
              const pair =
                relationPairs.find(
                  ([left, right]) =>
                    left === relation.type || right === relation.type,
                ) ?? [relation.type];
              const [left] = pair;

              edges.push({
                from: left === relation.type ? entityRef : relation.targetRef,
                to: left === relation.type ? relation.targetRef : entityRef,
                relations: pair,
                label: 'visible',
              });
            } else {
              edges.push({
                from: entityRef,
                to: relation.targetRef,
                relations: [relation.type],
                label: 'visible',
              });
            }
          }

          if (!visitedNodes.has(relation.targetRef)) {
            nodeQueue.push(relation.targetRef);
            visitedNodes.add(relation.targetRef);
          }

          if (unidirectional) {
            const index = edges.findIndex(
              edge =>
                edge.from === entityRef &&
                edge.to === relation.targetRef &&
                !edge.relations.includes(relation.type),
            );

            if (index >= 0) {
              if (mergeRelations) {
                const pair =
                  relationPairs.find(
                    ([left, right]) =>
                      left === relation.type || right === relation.type,
                  ) ?? [relation.type];
                edges[index].relations = [
                  ...edges[index].relations,
                  ...pair,
                ];
              } else {
                edges[index].relations = [
                  ...edges[index].relations,
                  relation.type,
                ];
              }
            }
          }
        });
      }

      const finalEdges = edges.reduce<EntityEdge[]>((acc, current) => {
        const matchIndex = acc.findIndex(
          edge => edge.from === current.from && edge.to === current.to,
        );

        if (matchIndex >= 0) {
          acc[matchIndex] = {
            ...acc[matchIndex],
            relations: Array.from(
              new Set([...acc[matchIndex].relations, ...current.relations]),
            ),
          };
          return acc;
        }

        return [...acc, current];
      }, []);

      setNodesAndEdges({ nodes, edges: finalEdges });
    },
    100,
    [
      entities,
      rootEntityRefs,
      kinds,
      relations,
      unidirectional,
      mergeRelations,
      onNodeClick,
      relationPairs,
    ],
  );

  return {
    loading,
    error,
    ...nodesAndEdges,
  };
};
